import React, { useState, useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import WarningIcon from '@atlaskit/icon/glyph/warning'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * CONFIRM DELETE MODAL
 *
 * Generic confirmation modal for delete operations.
 * Shows a warning and requires user confirmation.
 *
 * For critical operations (like release deletion), set requireTypeConfirmation=true
 * to require the user to type "delete" before confirming.
 */
function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Delete',
  itemName = 'item',
  itemType = 'item', // 'folder', 'scenario', or 'release'
  warningMessage,
  requireTypeConfirmation = false // When true, user must type "delete" to confirm
}) {
  const { showError } = useNotification()
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')

  // Reset confirmation text when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationText('')
    }
  }, [isOpen])

  // Check if delete button should be enabled
  const isDeleteEnabled = requireTypeConfirmation
    ? confirmationText.toLowerCase() === 'delete'
    : true

  // Default warning messages based on item type
  // If warningMessage is explicitly null, no warning will be shown
  const defaultWarning = itemType === 'folder'
    ? 'This will permanently delete the folder and all sub-folders. Any scenarios within this folder or its sub-folders will become unfoldered and appear under "All Scenarios".'
    : null

  // Handle confirm
  const handleConfirm = async () => {
    setIsDeleting(true)
    try {
      await onConfirm()
      onClose()
    } catch (error) {
      console.error('Delete failed:', error)
      const errorMessage = error?.message || 'Failed to delete. Please try again.'
      showError('Delete Failed', errorMessage)
      onClose()
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - pointer-events:none prevents click detection, Panel has pointer-events:auto */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '400px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-danger, #FFEBE6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <WarningIcon
                label="Warning"
                size="medium"
                primaryColor="var(--ds-icon-danger, #DE350B)"
              />
            </div>
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              {title}
            </Dialog.Title>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            <p style={{
              fontSize: '14px',
              color: 'var(--ds-text, #172B4D)',
              margin: '0 0 12px 0'
            }}>
              Are you sure you want to delete <strong>"{itemName}"</strong>?
            </p>
            {(warningMessage || defaultWarning) && (
              <p style={{
                fontSize: '13px',
                color: 'var(--ds-text-subtlest, #6B778C)',
                margin: 0,
                padding: '12px',
                backgroundColor: 'var(--ds-background-warning, #FFFAE6)',
                borderRadius: '4px',
                border: '1px solid var(--ds-border-warning, #FFE380)'
              }}>
                {warningMessage || defaultWarning}
              </p>
            )}

            {/* Type confirmation input - shown for critical operations */}
            {requireTypeConfirmation && (
              <div style={{ marginTop: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--ds-text, #172B4D)',
                    marginBottom: '8px'
                  }}
                >
                  Type <strong style={{ color: 'var(--ds-text-danger, #DE350B)' }}>delete</strong> to confirm:
                </label>
                <TextField
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type 'delete' here"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDeleteEnabled && !isDeleting) {
                      e.preventDefault()
                      handleConfirm()
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={onClose}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              appearance="danger"
              onClick={handleConfirm}
              isLoading={isDeleting}
              isDisabled={!isDeleteEnabled}
            >
              Delete
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { ConfirmDeleteModal }
